import React, { useState } from 'react';
import { DeployRequest, TargetFilter, RolloutStrategy } from '../../types/api';
import { Rocket, Plus, Minus, AlertCircle } from 'lucide-react';

interface DeploymentFormProps {
  onSubmit: (request: DeployRequest) => Promise<void>;
  featurePacks?: string[];
  loading?: boolean;
}

export function DeploymentForm({ onSubmit, featurePacks = [], loading = false }: DeploymentFormProps) {
  const [featureName, setFeatureName] = useState('');
  const [version, setVersion] = useState('');
  const [dryRun, setDryRun] = useState(true);

  // Target filter state
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [namePattern, setNamePattern] = useState('');
  const [language, setLanguage] = useState<string[]>([]);
  const [languageInput, setLanguageInput] = useState('');
  const [exclude, setExclude] = useState<string[]>([]);
  const [excludeInput, setExcludeInput] = useState('');

  // Rollout strategy state
  const [rolloutType, setRolloutType] = useState<'all' | 'percentage' | 'canary' | 'manual'>('all');
  const [rolloutValue, setRolloutValue] = useState(100);
  const [canaryRepos, setCanaryRepos] = useState<string[]>([]);
  const [canaryInput, setCanaryInput] = useState('');

  const [error, setError] = useState<string>('');

  const addTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const addLanguage = () => {
    if (languageInput && !language.includes(languageInput)) {
      setLanguage([...language, languageInput]);
      setLanguageInput('');
    }
  };

  const removeLanguage = (lang: string) => {
    setLanguage(language.filter((l) => l !== lang));
  };

  const addExclude = () => {
    if (excludeInput && !exclude.includes(excludeInput)) {
      setExclude([...exclude, excludeInput]);
      setExcludeInput('');
    }
  };

  const removeExclude = (repo: string) => {
    setExclude(exclude.filter((r) => r !== repo));
  };

  const addCanaryRepo = () => {
    if (canaryInput && !canaryRepos.includes(canaryInput)) {
      setCanaryRepos([...canaryRepos, canaryInput]);
      setCanaryInput('');
    }
  };

  const removeCanaryRepo = (repo: string) => {
    setCanaryRepos(canaryRepos.filter((r) => r !== repo));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!featureName) {
      setError('Feature name is required');
      return;
    }

    const targetFilter: TargetFilter = {};
    if (tags.length > 0) targetFilter.tags = tags;
    if (namePattern) targetFilter.namePattern = namePattern;
    if (language.length > 0) targetFilter.language = language;
    if (exclude.length > 0) targetFilter.exclude = exclude;

    const rollout: RolloutStrategy = {
      type: rolloutType,
    };
    if (rolloutType === 'percentage') {
      rollout.percentage = rolloutValue;
    } else if (rolloutType === 'canary') {
      rollout.canaryRepos = canaryRepos;
    }

    const request: DeployRequest = {
      featureName,
      version: version || undefined,
      targetFilter: Object.keys(targetFilter).length > 0 ? targetFilter : undefined,
      rollout,
      dryRun,
    };

    try {
      await onSubmit(request);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deploy');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <Rocket className="h-6 w-6 text-blue-600" />
        <h2 className="text-xl font-bold text-gray-900">Deploy Feature</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Feature Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Feature Name *
          </label>
          {featurePacks.length > 0 ? (
            <select
              value={featureName}
              onChange={(e) => setFeatureName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select a feature pack</option>
              {featurePacks.map((pack) => (
                <option key={pack} value={pack}>
                  {pack}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={featureName}
              onChange={(e) => setFeatureName(e.target.value)}
              placeholder="e.g., github-workflow"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Version
          </label>
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="e.g., 1.0.0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Target Filter */}
      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium text-gray-900">Target Repositories</h3>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Add tag"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={addTag}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg"
              >
                {tag}
                <button type="button" onClick={() => removeTag(tag)}>
                  <Minus className="h-4 w-4" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Name Pattern */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name Pattern (regex)
          </label>
          <input
            type="text"
            value={namePattern}
            onChange={(e) => setNamePattern(e.target.value)}
            placeholder="e.g., ^content-.*"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Languages */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Languages
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={languageInput}
              onChange={(e) => setLanguageInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())}
              placeholder="Add language"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={addLanguage}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {language.map((lang) => (
              <span
                key={lang}
                className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg"
              >
                {lang}
                <button type="button" onClick={() => removeLanguage(lang)}>
                  <Minus className="h-4 w-4" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Exclude */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exclude Repositories
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={excludeInput}
              onChange={(e) => setExcludeInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExclude())}
              placeholder="owner/repo"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={addExclude}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {exclude.map((repo) => (
              <span
                key={repo}
                className="inline-flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-lg"
              >
                {repo}
                <button type="button" onClick={() => removeExclude(repo)}>
                  <Minus className="h-4 w-4" />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Rollout Strategy */}
      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium text-gray-900">Rollout Strategy</h3>

        <div className="grid grid-cols-4 gap-2">
          {(['all', 'percentage', 'canary', 'manual'] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setRolloutType(type)}
              className={`px-4 py-2 rounded-lg font-medium capitalize ${
                rolloutType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {rolloutType === 'percentage' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Percentage: {rolloutValue}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={rolloutValue}
              onChange={(e) => setRolloutValue(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        )}

        {rolloutType === 'canary' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Canary Repositories
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={canaryInput}
                onChange={(e) => setCanaryInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCanaryRepo())}
                placeholder="owner/repo"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addCanaryRepo}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {canaryRepos.map((repo) => (
                <span
                  key={repo}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg"
                >
                  {repo}
                  <button type="button" onClick={() => removeCanaryRepo(repo)}>
                    <Minus className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Dry Run */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="dryRun"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="dryRun" className="text-sm font-medium text-gray-700">
          Dry Run (preview changes without applying)
        </label>
      </div>

      {/* Submit */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Deploying...' : dryRun ? 'Preview Deployment' : 'Deploy Feature'}
        </button>
      </div>
    </form>
  );
}
